// The game renderer: WebGL2 context, world scene, viewmodel scene, post-processing and the
// sub-renderers the game session drives (docs/INTERNALS.md "Client module interfaces").

import * as THREE from "three";
import { pixelRatioFor, verticalFovDeg } from "./cameraMath";
import { Effects } from "./effects";
import { ENV_INTENSITY, SceneLighting } from "./lighting";
import { buildMap } from "./mapScene";
import { createCharacter } from "./models/character";
import type { CharacterRig } from "./models/character";
import { createWeaponModel } from "./models/weapons";
import type { WeaponModel } from "./models/weapons";
import { yieldToEventLoop } from "./yield";
import type { BuiltMap } from "./mapScene";
import type { GlassPanes } from "./glass";
import type { MapEnv, MapFileJson, MapGrade } from "./mapTypes";
import { setTextureScale, texturesReady } from "./materials/textures";
import { ObjectiveManager } from "./objectives";
import { PlayerManager } from "./players";
import { PostStack } from "./post";
import { ProjectileManager } from "./projectiles";
import { qualityPreset } from "./quality";
import type { QualityPreset } from "./quality";
import { Sky } from "./sky";
import type {
  EffectsRenderer,
  ObjectiveRenderer,
  PlayerRenderer,
  ProjectileRenderer,
  Vec3Like,
  ViewmodelRenderer,
} from "./types";
import { Viewmodel } from "./viewmodel";
import { zoneTime } from "./zones";
import { Sec, perfBegin, perfEnd, perfMark, probe } from "../perf/probe";
import type { Settings } from "../settings/schema";
import type { RenderMeshData } from "../wasm";

type Video = Settings["video"];

/** Share of a map load spent building geometry and textures; the shader warm-up takes the rest. */
const BUILD_SHARE = 0.6;

/** `?shaderErrors` keeps three's shader error reporting on in a production build (see the constructor). */
const SHADER_ERRORS = typeof location !== "undefined" && new URLSearchParams(location.search).has("shaderErrors");

export interface LoadOptions {
  /** 0..1 over the whole load. */
  onProgress?: (fraction: number) => void;
  cancelled?: () => boolean;
}

/** Used before the first map is loaded (menus, previews). */
const DEFAULT_ENV: MapEnv = {
  skyTop: "#7fb2e8",
  skyHorizon: "#dbe6ef",
  skyBottom: "#8d8c88",
  fogColor: "#dbe6ef",
  fogNear: 60,
  fogFar: 220,
  sunDir: [0.4, 0.8, 0.3],
  sunColor: "#ffe1b5",
  sunIntensity: 3.2,
  ambientSky: "#cfe0f2",
  ambientGround: "#8d8c88",
  ambientIntensity: 1.1,
  grade: { exposure: 1, contrast: 0.3, saturation: 1.1, shadowTint: "#4a76c6", highlightTint: "#fff0d8" },
};

/** Creates the WebGL2 context (low-latency hints first, then without `desynchronized`). */
function createContext(canvas: HTMLCanvasElement): WebGL2RenderingContext {
  const attrs: WebGLContextAttributes = {
    alpha: false,
    antialias: false,
    depth: true,
    stencil: false,
    powerPreference: "high-performance",
    preserveDrawingBuffer: false,
    desynchronized: true,
  };
  const gl = canvas.getContext("webgl2", attrs) ?? canvas.getContext("webgl2", { ...attrs, desynchronized: false });
  if (gl === null) throw new Error("WebGL2 is not available in this browser");
  return gl;
}

export class Renderer {
  readonly canvas: HTMLCanvasElement;
  /** The three.js renderer (exposed for tooling; the game session does not need it). */
  readonly gl: THREE.WebGLRenderer;
  readonly scene = new THREE.Scene();
  readonly camera = new THREE.PerspectiveCamera(75, 16 / 9, 0.05, 400);

  readonly viewmodel: ViewmodelRenderer;
  readonly players: PlayerRenderer;
  readonly effects: EffectsRenderer;
  readonly projectiles: ProjectileRenderer;
  readonly objectives: ObjectiveRenderer;

  private readonly vm: Viewmodel;
  private readonly playerMgr: PlayerManager;
  private readonly effectMgr: Effects;
  private readonly projectileMgr: ProjectileManager;
  private readonly objectiveMgr: ObjectiveManager;
  private readonly lighting: SceneLighting;
  private readonly sky = new Sky();
  private readonly observer: ResizeObserver | null;

  private video: Video;
  private preset: QualityPreset;
  private post: PostStack | null = null;
  private grade: MapGrade = DEFAULT_ENV.grade;
  /** A hidden remote-player rig that keeps the character shader programs alive between maps. */
  private warmRig: { rig: CharacterRig; weapon: WeaponModel } | null = null;
  private built: BuiltMap | null = null;
  private hFov = 103;
  private aspectRatio = 16 / 9;
  private needsResize = true;
  private shadowDirty = true;
  private time = 0;
  private frame = 0;
  /** Number of the newest `loadMap` call; an older one still running gives up. */
  private loadId = 0;
  private fps = 60;

  constructor(canvas: HTMLCanvasElement, video: Video) {
    this.canvas = canvas;
    this.video = video;
    this.preset = qualityPreset(video.quality);

    const context = createContext(canvas);
    const gl = new THREE.WebGLRenderer({
      canvas,
      context: context as unknown as WebGLRenderingContext,
      antialias: false,
      alpha: false,
      stencil: false,
    });
    this.gl = gl;
    probe?.expose("three", { gl, scene: this.scene, camera: this.camera });
    probe?.attach(
      context,
      () => ({ calls: gl.info.render.calls, triangles: gl.info.render.triangles }),
      () => ({
        programs: gl.info.programs?.length ?? 0,
        geometries: gl.info.memory.geometries,
        textures: gl.info.memory.textures,
        sceneChildren: this.scene.children.length,
      }),
    );
    // three reads the program and shader info logs when a program is first used; each read waits for the
    // driver to finish compiling and linking it, on the main thread (hundreds of ms for the programs the
    // first render still has to build). Error reporting stays on in dev builds and with `?shaderErrors`.
    gl.debug.checkShaderErrors = import.meta.env.DEV || SHADER_ERRORS;
    gl.outputColorSpace = THREE.SRGBColorSpace;
    gl.toneMapping = THREE.NeutralToneMapping;
    gl.toneMappingExposure = 1;
    gl.shadowMap.enabled = true;
    gl.shadowMap.type = THREE.PCFShadowMap;
    gl.shadowMap.autoUpdate = false;
    // The frame is several render calls; accumulate the stats and reset once per frame.
    gl.info.autoReset = false;

    this.camera.rotation.order = "YXZ";
    this.lighting = new SceneLighting(this.scene);
    this.scene.add(this.sky.mesh);
    this.vm = new Viewmodel(this.camera);
    this.effectMgr = new Effects(this.scene, this.vm, this.lighting.fx);
    this.playerMgr = new PlayerManager(this.scene);
    this.projectileMgr = new ProjectileManager(this.scene);
    this.objectiveMgr = new ObjectiveManager();
    this.scene.add(this.objectiveMgr.group);
    this.viewmodel = this.vm;
    this.players = this.playerMgr;
    this.effects = this.effectMgr;
    this.projectiles = this.projectileMgr;
    this.objectives = this.objectiveMgr;

    this.applyEnvironment(DEFAULT_ENV, new THREE.Box3(new THREE.Vector3(-60, 0, -60), new THREE.Vector3(60, 30, 60)));
    this.applySettings(video);

    this.observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(() => {
            this.needsResize = true;
          });
    this.observer?.observe(canvas);
    this.resize();
  }

  /** Width / height of the canvas as of the last resize. */
  get aspect(): number {
    return this.aspectRatio;
  }

  applySettings(video: Video): void {
    const prev = this.video;
    this.video = video;
    this.preset = qualityPreset(video.quality);
    setTextureScale(this.preset.textureScale);
    this.lighting.applyQuality(this.preset, video.shadows);
    this.effectMgr.setQuality(this.preset);
    this.vm.setEnabled(video.showViewmodel);
    this.built?.setAnisotropy(this.preset.anisotropy);
    if (this.preset.post) {
      if (this.post === null) {
        this.post = new PostStack(this.gl, this.scene, this.camera, this.vm.scene, this.vm.camera, () => this.vm.active, this.grade);
        this.needsResize = true;
      }
      if (this.post.configure(video, this.preset)) this.needsResize = true;
    } else if (this.post !== null) {
      this.post.dispose();
      this.post = null;
    }
    // Settings change continuously while a slider is dragged: only what a changed value affects is redone.
    if (video.renderScale !== prev.renderScale || video.quality !== prev.quality) this.needsResize = true;
    else if (video.viewmodelFov !== prev.viewmodelFov) this.applyProjection();
    if (video.quality !== prev.quality || video.shadows !== prev.shadows) this.shadowDirty = true;
  }

  /**
   * Replaces any previous map (and the decals, smoke and projectiles that belonged to it), builds
   * the new one in slices and compiles every shader the match will draw (world, remote players
   * with a weapon, viewmodel, post-processing) so the first frames do not stall. The event loop
   * keeps running throughout. Resolves to false when `opts.cancelled` turned true or another
   * `loadMap` superseded this one.
   */
  async loadMap(map: MapFileJson, mesh: RenderMeshData, opts: LoadOptions = {}): Promise<boolean> {
    const id = ++this.loadId;
    const cancelled = (): boolean => id !== this.loadId || (opts.cancelled?.() ?? false);
    perfMark("load-start");
    this.unloadMap();
    this.effectMgr.clear();
    this.projectileMgr.clear();
    const built = await buildMap(map, mesh, { onProgress: (f) => opts.onProgress?.(f * BUILD_SHARE), cancelled });
    if (built === null) return false;
    perfMark("build-done");
    // The surface textures paint in workers while the shaders compile below.
    const texturesDone = texturesReady();
    if (cancelled()) {
      built.dispose();
      return false;
    }
    built.setAnisotropy(this.preset.anisotropy);
    this.built = built;
    this.scene.add(built.root);
    this.applyEnvironment(map.env, built.bounds);
    this.objectiveMgr.setMap(map);
    this.shadowDirty = true;
    opts.onProgress?.(BUILD_SHARE);
    await yieldToEventLoop();
    perfMark("warm-start");
    if (cancelled()) return false;
    await this.warmUp(cancelled, (f) => opts.onProgress?.(BUILD_SHARE + (1 - BUILD_SHARE) * f));
    await texturesDone;
    perfMark("textures-done");
    return !cancelled();
  }

  /**
   * Gets every shader the match draws compiled before the first frame, without holding the main
   * thread for the whole compile (see `compileMaterials`, `compileFrameDraws`). All the compiles are
   * requested first and awaited together, so the driver links them in parallel. Warm-up only saves
   * hitches, so a failure here is logged and the match goes on.
   */
  private async warmUp(cancelled: () => boolean, progress: (fraction: number) => void): Promise<void> {
    try {
      // The world scene with a remote player holding a weapon (their materials are not in it yet).
      // The rig stays in the scene, hidden: disposing it would release the character programs again.
      if (this.warmRig === null) {
        const weapon = createWeaponModel("kr7", [], "none");
        const rig = createCharacter({ team: "a", enemy: true });
        rig.setWeapon(weapon.root);
        this.warmRig = { rig, weapon };
      }
      const { rig } = this.warmRig;
      rig.root.visible = true;
      this.scene.add(rig.root);
      // The first-person weapon (the knife shows on a melee) and the flash overlay.
      this.vm.setLoadoutWeapon("knife", [], "none");
      this.vm.setLoadoutWeapon("kr7", [], "none");
      const inputBuffer = this.post?.inputBuffer ?? null;
      try {
        await Promise.all([
          this.compileMaterials(this.scene, this.camera, this.scene, cancelled, inputBuffer),
          this.compileMaterials(this.vm.scene, this.vm.camera, this.vm.scene, cancelled, inputBuffer),
          this.compileMaterials(this.effectMgr.overlayScene, this.effectMgr.overlayCamera, this.effectMgr.overlayScene, cancelled, null),
          this.compileFrameDraws(cancelled),
        ]);
        perfMark("warm-compiled");
      } finally {
        rig.root.visible = false;
      }
      if (cancelled()) return;
      progress(0.8);
      await yieldToEventLoop();
      if (cancelled()) return;
      if (probe) perfMark(`programs-before-first-render=${this.gl.info.programs?.length ?? 0}`);
      this.render(0);
      if (probe) perfMark(`programs-after-first-render=${this.gl.info.programs?.length ?? 0}`);
      perfMark("warm-first-render");
      progress(1);
    } catch (err) {
      console.warn("renderer: shader warm-up failed", err);
    }
  }

  /**
   * Links what `compileMaterials` cannot reach by walking the scenes: the shadow depth programs (three
   * derives a depth material per draw) and the post-processing chain's full-screen passes. One frame is
   * run with `renderBufferDirect` recording each distinct draw instead of drawing, then a stand-in mesh
   * per draw goes through `compileAsync` for the render target and scene it was drawn with. Without
   * this the first real frame builds those programs one at a time, waiting for each link (about
   * 100 ms apiece on ANGLE over Mesa d3d12; 7 of them at High).
   */
  private compileFrameDraws(cancelled: () => boolean): Promise<void> {
    const gl = this.gl;
    if (cancelled() || gl.getContext().getExtension("KHR_parallel_shader_compile") === null) return Promise.resolve();
    type Standable = THREE.Mesh | THREE.Points | THREE.Line | THREE.Sprite;
    type Draw = { stand: Standable; scene: THREE.Scene | null; target: THREE.WebGLRenderTarget | null };
    const draws = new Map<string, Draw>();
    const original = gl.renderBufferDirect;
    gl.renderBufferDirect = (_camera, scene, _geometry, material, object) => {
      if (!(object instanceof THREE.Mesh || object instanceof THREE.Points || object instanceof THREE.Line || object instanceof THREE.Sprite)) return;
      const target = gl.getRenderTarget();
      const key = [
        scene?.uuid,
        target?.texture.uuid,
        material.uuid,
        material.type,
        material.side,
        material.alphaTest,
        material instanceof THREE.MeshDepthMaterial ? material.map?.uuid : "",
        object.type,
      ].join("|");
      if (draws.has(key)) return;
      const stand = object.clone(false);
      // Three reuses and rewrites one depth material between draws: keep a copy of this draw's state.
      stand.material = material instanceof THREE.MeshDepthMaterial ? material.clone() : material;
      stand.visible = true;
      stand.frustumCulled = false;
      draws.set(key, { stand, scene, target });
    };
    try {
      this.render(0);
    } finally {
      gl.renderBufferDirect = original;
      // The recording frame consumed the shadow refresh; the first real frame still has to draw it.
      this.shadowDirty = true;
    }
    const previous = gl.getRenderTarget();
    const ready: Promise<unknown>[] = [];
    const groups = new Map<string, { scene: THREE.Scene | null; target: THREE.WebGLRenderTarget | null; stands: Standable[] }>();
    for (const d of draws.values()) {
      const key = `${d.scene?.uuid}|${d.target?.texture.uuid}`;
      const group = groups.get(key) ?? { scene: d.scene, target: d.target, stands: [] };
      group.stands.push(d.stand);
      groups.set(key, group);
    }
    for (const { scene, target, stands } of groups.values()) {
      const holder = new THREE.Scene();
      holder.add(...stands);
      // A shadow draw has no scene of its own: three gives it an empty one (no fog) next to the world's lights.
      const world = scene ?? this.scene;
      const fog = world.fog;
      if (scene === null) world.fog = null;
      gl.setRenderTarget(target);
      ready.push(gl.compileAsync(holder, this.camera, world));
      world.fog = fog;
    }
    gl.setRenderTarget(previous);
    return Promise.all(ready).then(() => undefined);
  }

  /**
   * Compiles the materials under `root` as seen from `target` (its lights and fog), for drawing into
   * `renderTarget` (null = the canvas): three keys a program on the colour space and tone mapping of
   * the current target, so compiling for the wrong one only builds programs that are never used and
   * leaves the real ones to link on the first frame. With KHR_parallel_shader_compile the browser
   * links the programs on other threads and `compileAsync` just waits. Without it (ANGLE on many
   * Windows GPUs) `compile` only submits and the first draw would wait for all the links at once,
   * so each program is linked here instead, one per slice with a yield in between: the longest
   * stall is one program, not all of them.
   */
  private async compileMaterials(
    root: THREE.Object3D,
    camera: THREE.Camera,
    target: THREE.Scene,
    cancelled: () => boolean,
    renderTarget: THREE.WebGLRenderTarget | null,
  ): Promise<void> {
    const gl = this.gl;
    const previous = gl.getRenderTarget();
    if (gl.getContext().getExtension("KHR_parallel_shader_compile") !== null) {
      gl.setRenderTarget(renderTarget);
      const ready = gl.compileAsync(root, camera, target);
      gl.setRenderTarget(previous);
      await ready;
      return;
    }
    const seen = new Set<THREE.Material>();
    const carriers: { object: THREE.Object3D; materials: THREE.Material[] }[] = [];
    root.traverse((o) => {
      if (!(o instanceof THREE.Mesh || o instanceof THREE.Points || o instanceof THREE.Line || o instanceof THREE.Sprite)) return;
      const all: THREE.Material[] = Array.isArray(o.material) ? o.material : [o.material];
      const fresh = all.filter((m) => !seen.has(m));
      for (const m of fresh) seen.add(m);
      if (fresh.length > 0) carriers.push({ object: o, materials: fresh });
    });
    for (const { object, materials } of carriers) {
      if (cancelled()) return;
      gl.setRenderTarget(renderTarget);
      gl.compile(object, camera, target);
      gl.setRenderTarget(previous);
      for (const m of materials) {
        const program = (gl.properties.get(m) as { currentProgram?: { getUniforms(): unknown } } | undefined)?.currentProgram;
        program?.getUniforms(); // waits for the link, as the first draw would
      }
      await yieldToEventLoop();
    }
  }

  private unloadMap(): void {
    if (this.built === null) return;
    this.built.dispose();
    this.built = null;
  }

  /** The breakable glass panes of the loaded map; null before a map is loaded or when it has none. */
  get glass(): GlassPanes | null {
    return this.built?.glass ?? null;
  }

  private applyEnvironment(env: MapEnv, bounds: THREE.Box3): void {
    this.sky.setEnv(env);
    const envTexture = this.sky.buildEnvironment(this.gl, env);
    this.scene.environment = envTexture;
    this.scene.environmentIntensity = ENV_INTENSITY;
    if (this.scene.fog instanceof THREE.Fog) {
      this.scene.fog.color.set(env.fogColor);
      this.scene.fog.near = env.fogNear;
      this.scene.fog.far = env.fogFar;
    } else {
      this.scene.fog = new THREE.Fog(env.fogColor, env.fogNear, env.fogFar);
    }
    this.gl.setClearColor(env.skyHorizon);
    // Exposure applies at every preset: the composer path has its own copy of it (post.ts).
    this.grade = env.grade;
    this.gl.toneMappingExposure = env.grade.exposure;
    this.post?.setGrade(env.grade);
    this.lighting.configure(env, bounds);
    this.vm.setLighting(this.lighting.sun, this.lighting.hemi, envTexture, ENV_INTENSITY);
    const diag = bounds.getSize(new THREE.Vector3()).length();
    this.camera.far = Math.max(150, diag * 1.3 + 50);
    this.camera.updateProjectionMatrix();
  }

  /** `yaw` / `pitch` / `roll` in radians (YXZ), `hFovDeg` is the horizontal FOV at 16:9. */
  setCamera(pos: Vec3Like, yaw: number, pitch: number, roll: number, hFovDeg: number): void {
    this.camera.position.set(pos.x, pos.y, pos.z);
    this.camera.rotation.set(pitch, yaw, roll);
    if (hFovDeg !== this.hFov) {
      this.hFov = hFovDeg;
      this.applyProjection();
    }
    this.camera.updateMatrixWorld(true);
  }

  private applyProjection(): void {
    this.camera.fov = verticalFovDeg(this.hFov, this.aspectRatio);
    this.camera.aspect = this.aspectRatio;
    this.camera.updateProjectionMatrix();
    this.vm.setProjection(this.aspectRatio, this.video.viewmodelFov);
  }

  resize(): void {
    this.needsResize = false;
    const w = this.canvas.clientWidth;
    const h = this.canvas.clientHeight;
    if (w < 1 || h < 1) return;
    const ratio = pixelRatioFor(this.video.renderScale, globalThis.devicePixelRatio ?? 1, w, h);
    this.gl.setPixelRatio(ratio);
    this.gl.setSize(w, h, false);
    this.post?.setSize(w, h);
    this.aspectRatio = w / h;
    this.applyProjection();
  }

  render(frameDt: number): void {
    const dt = frameDt > 0 ? Math.min(frameDt, 0.1) : 0;
    this.time += dt;
    this.frame++;
    if (frameDt > 0) this.fps += (1 / Math.max(frameDt, 1e-4) - this.fps) * Math.min(1, frameDt * 3);
    if (this.needsResize) this.resize();

    perfBegin(Sec.rPrep);
    zoneTime.value = this.time;
    this.sky.follow(this.camera);
    this.objectiveMgr.update(dt);
    this.projectileMgr.update(dt);
    this.effectMgr.frame(dt);
    this.vm.setSunDirection(this.lighting.sunDir);

    // The shadow map holds static geometry and the moving casters: refresh it on load and every few
    // frames while players or projectiles exist.
    if (this.lighting.shadows) {
      const dynamic = this.playerMgr.count > 0 || this.projectileMgr.count > 0;
      if (this.shadowDirty || (dynamic && this.frame % this.preset.shadowEvery === 0)) {
        this.gl.shadowMap.needsUpdate = true;
        this.shadowDirty = false;
      }
    }
    perfEnd(Sec.rPrep);

    const gl = this.gl;
    perfBegin(Sec.rDraw);
    probe?.gpuBegin();
    gl.info.reset();
    if (this.post !== null) {
      this.post.render(dt);
    } else {
      gl.setRenderTarget(null);
      gl.autoClear = true;
      gl.render(this.scene, this.camera);
      if (this.vm.active) {
        gl.autoClear = false;
        gl.clearDepth();
        gl.render(this.vm.scene, this.vm.camera);
      }
    }
    perfEnd(Sec.rDraw);
    if (this.effectMgr.overlayVisible) {
      perfBegin(Sec.rOverlay);
      gl.setRenderTarget(null);
      gl.autoClear = false;
      gl.render(this.effectMgr.overlayScene, this.effectMgr.overlayCamera);
      perfEnd(Sec.rOverlay);
    }
    probe?.gpuEnd();
  }

  info(): { fps: number; drawCalls: number; triangles: number } {
    const r = this.gl.info.render;
    return { fps: this.fps, drawCalls: r.calls, triangles: r.triangles };
  }

  dispose(): void {
    this.observer?.disconnect();
    this.playerMgr.clear();
    if (this.warmRig !== null) {
      this.warmRig.rig.setWeapon(null);
      this.warmRig.rig.root.removeFromParent();
      this.warmRig.rig.dispose();
      this.warmRig.weapon.dispose();
      this.warmRig = null;
    }
    this.projectileMgr.clear();
    this.objectiveMgr.dispose();
    this.effectMgr.dispose();
    this.vm.dispose();
    this.unloadMap();
    this.post?.dispose();
    this.post = null;
    this.sky.dispose();
    this.lighting.dispose();
    // The canvas outlives the renderer, so the WebGL context is kept (no forceContextLoss).
    this.gl.dispose();
  }
}
